/* eslint-disable no-empty-pattern */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Button, 
  Title, 
  FlexBox, 
  FlexBoxDirection, 
  Link, 
  Icon, 
  BusyIndicator
} from '@ui5/webcomponents-react';
import Swal from 'sweetalert2';
import useAutoTranslate from '../hooks/useAutoTranslate';
import './Login.css';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const Login = ({ }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_URL;

  const { autoT } = useAutoTranslate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const apiUrl =  `${baseUrl}auth/login`;
    const loginData = {
        email: username,
        password: password
    };

    try {
        const response = await axios.post(apiUrl, loginData, {
            headers: {
                'accept': '*/*',
                'Content-Type': 'application/json'
            }
        });

        if (response.data.code === 'SUCCESS') {
            console.log('Datos recibidos:', response.data.data);
            const userData = response.data.data.user;
            let companyDisplayName = '';

            if (userData.companyTypeName === 'MANDANTE' && userData.companies && userData.companies.length > 0) {
              companyDisplayName = userData.companies[0].company.name;
            } else if (userData.companyTypeName === 'PROVEEDOR' && userData.suppliers && userData.suppliers.length > 0) {
              companyDisplayName = userData.suppliers[0].supplier.supplierName || userData.suppliers[0].supplier.name || userData.suppliers[0].supplier.businessName;
            }

            localStorage.setItem('userData', JSON.stringify(userData));
            localStorage.setItem('token', userData.bearerToken);
            localStorage.setItem('userCompanyDisplay', companyDisplayName);
            
            Toast.fire({
              icon: 'success',
              title: autoT('Bienvenido {{name}}!', { name: userData.name })
            });
            setTimeout(() => {
               navigate('/home', { state: { activePart: 'home' } });
            }, 1000);
        } else {
            Toast.fire({
              icon: 'error',
              title: autoT('Error en el inicio de sesión')
            });
            console.error('Error en la respuesta:', response.data);
        }
    } catch (error) {
        Toast.fire({
          icon: 'error',
          title: autoT('Error al conectar con el servidor')
        });
        console.error('Error:', error);
    } finally {
        setIsLoading(false);
    }
  };

  const handlePasswordRecovery = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${baseUrl}notifications/recover-password?email=${encodeURIComponent(recoveryEmail)}`);
      if (response.data.code === 'SUCCESS') {
        Toast.fire({
          icon: 'success',
          title: autoT('Se ha enviado un correo para recuperar la contraseña')
        });
        setIsRecoveringPassword(false);
      } else {
        console.log("error");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="login-shell">
      <header className="login-topbar">
        <div className="login-topbar__brand">
          <span>
            {autoT('Smart Shipping')}
          </span>
          <span>
            {autoT('Faros Logistics Network')}
          </span>
        </div>
      </header>

      <main className="login-main">
        <section className="login-frame" aria-label={autoT('FAROS SMART SHIPPING')}>
          <div className="login-hero-panel">
            <img
              className="login-hero-panel__image"
              src="https://ucarecdn.com/cf307f44-3c2d-418d-8278-7d172e3b9653/FarosSuites.png"
              alt="FAROS Logistics Network"
            />
          </div>

          <aside className="login-access-panel">
            <div className="login-card">
              <div className="login-logo">
                <img
                  src="faros_12_shipping.png"
                  alt="Logo FAROS"
                />
              </div>

              <Title level="H2" className="login-title">
                {autoT('FAROS SMART SHIPPING')}
              </Title>
              <p className="login-subtitle">
                {autoT('Inicia sesión para acceder a tu cuenta')}
              </p>

              {isRecoveringPassword ? (
                <form className="login-form" onSubmit={handlePasswordRecovery}>
                  <FlexBox direction={FlexBoxDirection.Column} className="login-form-stack">
                    <div className="login-field">
                      <label htmlFor="recoveryEmail">{autoT('Correo Electrónico')}</label>
                      <div className="login-input">
                        <Icon name="email" className="login-input__icon" />
                        <input
                          id="recoveryEmail"
                          type="email"
                          placeholder={autoT('Correo Electrónico')}
                          value={recoveryEmail}
                          onChange={(e) => setRecoveryEmail(e.target.value)}
                        />
                      </div>
                    </div>

                    <Button
                      className="login-submit"
                      design="Emphasized"
                      type="Submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="login-loading">
                          <BusyIndicator active="true" size="S" />
                          <span>{autoT('Enviando...')}</span>
                        </span>
                      ) : (
                        autoT('Recuperar Contraseña')
                      )}
                    </Button>

                    <div className="login-secondary-action">
                      <Link onClick={() => setIsRecoveringPassword(false)}>
                        {autoT('Volver al inicio de sesión')}
                      </Link>
                    </div>
                  </FlexBox>
                </form>
              ) : (
                <form className="login-form" onSubmit={handleLogin}>
                  <FlexBox direction={FlexBoxDirection.Column} className="login-form-stack">
                    <div className="login-field">
                      <label htmlFor="username">{autoT('Usuario')}</label>
                      <div className="login-input">
                        <Icon name="customer" className="login-input__icon" />
                        <input
                          id="username"
                          placeholder={autoT('Usuario')}
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="login-field">
                      <label htmlFor="password">{autoT('Contraseña')}</label>
                      <div className="login-input">
                        <Icon name="locked" className="login-input__icon" />
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={autoT('Contraseña')}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="login-password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          title={showPassword ? autoT('Ocultar') : autoT('Mostrar')}
                          aria-label={showPassword ? autoT('Ocultar') : autoT('Mostrar')}
                        >
                          <Icon name={showPassword ? 'hide' : 'show'} />
                        </button>
                      </div>
                    </div>

                    <Button
                      className="login-submit"
                      design="Emphasized"
                      type="Submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="login-loading">
                          <BusyIndicator active="true" size="S" />
                          <span>{autoT('Iniciando...')}</span>
                        </span>
                      ) : (
                        <span className="login-button-content">
                          <Icon name="action" />
                          <span>{autoT('Iniciar Sesión')}</span>
                        </span>
                      )}
                    </Button>

                    <div className="login-secondary-action">
                      <Link onClick={() => setIsRecoveringPassword(true)}>
                        {autoT('Recuperar Contraseña')}
                      </Link>
                    </div>
                  </FlexBox>
                </form>
              )}
            </div>

            <footer className="login-footer">
              © 2026 Faros Logistics Network. Todos los derechos reservados.
            </footer>
          </aside>
        </section>
      </main>
    </div>
  );
}

export default Login;